import { useState } from "react";
import { useDebugLog } from "../../hooks/useDebugLog";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import Loader from "../Loader";
import { getLogged } from "../../redux/userSlice";
import { useTranslation } from 'react-i18next';
import { setBacklink, resetBacklink } from '../../redux/backlinkSlice.js';
import { Eye, EyeOff, Mail, Lock, User } from "lucide-react";

// Stessa regex usata dal backend in addNewUser (userControllers.js)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Classi condivise per gli input restylati (override delle regole globali in index.css)
const INPUT_BASE = "w-full !py-3.5 !pl-12 !rounded-xl !border !border-neutral-200 bg-neutral-50 placeholder:text-neutral-400 focus:!border-[#225424] focus:bg-white text-base outline-none";

const RegisterForm = ({ setSection }) => {
    const { t } = useTranslation();
    const [inputData, setInputData] = useState({
        name: '',
        email: '',
        password: '',
        isTermsAccepted: false
    })

    const trimmedEmail = inputData.email.trim();
    const isEmailValid = EMAIL_REGEX.test(trimmedEmail);
    const showEmailHint = inputData.email.length > 0 && !isEmailValid;
    const isFormValid = inputData.name && isEmailValid && inputData.password && inputData.isTermsAccepted;
    const handleInputData = (event) => {
        const { id, value, checked } = event.target;
        if (id === 'isTermsAccepted') {
            setInputData(prevState => ({
                ...prevState,
                isTermsAccepted: checked
            }))
        } else {
            setInputData(prevState => ({
                ...prevState,
                [id]: value
            }))
        }
    }

    // Send register fetch
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const backlink = useSelector(state => state.backlink.link);
    const [fetchStatus, setFetchStatus] = useState('idle');
    const [error, setError] = useState(null);
    const sendRegisterData = async () => {
        try {
            setFetchStatus('loading');
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/register`;
            const headers = { 'Content-Type': 'application/json' };
            const payload = { ...inputData, email: inputData.email.trim() };
            const options = { method: 'POST', headers, body: JSON.stringify(payload) };
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setFetchStatus('succeeded');
                localStorage.setItem('ticino-wild-token', result.payload.token);
                dispatch(getLogged(result.payload));
                // Backlink coerente con LoginForm: se l'utente era stato indirizzato a /register-login
                // da un'azione che richiede login (es. click ❤️ da sloggato), torna a quel target.
                const destination = backlink || "/";
                dispatch(resetBacklink());
                setTimeout(() => {
                    navigate(destination, { replace: true });
                }, 3000);
            } else {
                const error = await response.json();
                setFetchStatus('failed');
                setError(error.message);
            }
        } catch (error) {
            console.log('Catch error: ', error);
            setFetchStatus('failed');
            setError(error.message);
        }
    }
    useDebugLog(error, 'error: ', error);

    // Gestisco il click su 'termini e condizioni'
    const handleTermsClick = () => {
        dispatch(setBacklink("/register-login?section=Register"));
        navigate("/terms-and-conditions");
    }

    // Handle password show
    const [isShowPassword, setIsShowPassword] = useState(false);

    return (
        <div className="w-full flex flex-col">
            {fetchStatus === 'idle' && (
                <>
                    {/* Mobile: titolo unico stilato come il sottotitolo desktop per guadagnare spazio */}
                    <h2 className="md:hidden !text-base !font-semibold text-[#225424] text-center mb-6 leading-relaxed">
                        {t('RegisterLoginPage.RegisterForm.mobileTitle')}
                    </h2>
                    {/* Desktop: titolo + sottotitolo */}
                    <h2 className="hidden md:block !text-3xl !font-bold text-[#225424] text-center">
                        {t('RegisterLoginPage.RegisterForm.heroTitle')}
                    </h2>
                    <p className="hidden md:block mt-2 mb-6 text-sm text-neutral-600 text-center leading-relaxed">
                        {t('RegisterLoginPage.RegisterForm.heroSubtitle')}
                    </p>

                    {/* Nome */}
                    <div className="relative mb-3">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#b05704] pointer-events-none" />
                        <input
                            type="text"
                            id="name"
                            autoComplete="name"
                            placeholder={t('RegisterLoginPage.RegisterForm.userNamePlaceholder')}
                            className={`${INPUT_BASE} !pr-4`}
                            value={inputData.name}
                            onChange={handleInputData}
                        />
                    </div>

                    {/* Email */}
                    <div className="relative mb-1">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#b05704] pointer-events-none" />
                        <input
                            type="email"
                            id="email"
                            autoComplete="email"
                            inputMode="email"
                            placeholder={t('RegisterLoginPage.RegisterForm.emailPlaceholder')}
                            className={`${INPUT_BASE} !pr-4`}
                            value={inputData.email}
                            onChange={handleInputData}
                        />
                    </div>
                    {showEmailHint && (
                        <p className="text-xs text-red-600 mb-1 pl-2">
                            {t('RegisterLoginPage.RegisterForm.emailInvalid')}
                        </p>
                    )}

                    {/* Password */}
                    <div className="relative mb-4 mt-3">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#b05704] pointer-events-none" />
                        <input
                            type={isShowPassword ? "text" : "password"}
                            id="password"
                            autoComplete="new-password"
                            placeholder={t('RegisterLoginPage.RegisterForm.passwordPlaceholder')}
                            className={`${INPUT_BASE} !pr-12`}
                            value={inputData.password}
                            onChange={handleInputData}
                        />
                        <button
                            type="button"
                            aria-label={isShowPassword ? 'hide password' : 'show password'}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-500"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setIsShowPassword(p => !p);
                            }}
                        >
                            {isShowPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>

                    {/* Termini */}
                    <label className="flex items-start gap-3 mb-5 text-sm text-neutral-700 cursor-pointer">
                        <input
                            type="checkbox"
                            id="isTermsAccepted"
                            checked={inputData.isTermsAccepted}
                            onChange={handleInputData}
                            className="!p-0 !w-5 !h-5 !rounded !border-2 !border-neutral-300 mt-0.5 flex-shrink-0 cursor-pointer"
                        />
                        <span className="leading-snug">
                            {t('RegisterLoginPage.RegisterForm.termsAndConditions.firstPart')}{' '}
                            <span
                                className="underline text-[#225424] font-semibold"
                                onClick={(e) => { e.preventDefault(); handleTermsClick(); }}
                            >
                                {t('RegisterLoginPage.RegisterForm.termsAndConditions.secondPart')}
                            </span>{' '}
                            {t('RegisterLoginPage.RegisterForm.termsAndConditions.thirdPart')}
                        </span>
                    </label>

                    <button
                        type="button"
                        onClick={sendRegisterData}
                        disabled={!isFormValid}
                        className={`w-full py-3.5 rounded-xl font-semibold text-base transition ${isFormValid
                            ? 'bg-[#b05704] text-white hover:bg-[#964804] active:bg-[#964804]'
                            : 'bg-neutral-200 text-neutral-400 cursor-default'
                            }`}
                    >
                        {t('RegisterLoginPage.RegisterForm.registerButton')}
                    </button>

                    <div className="flex items-center my-5">
                        <div className="flex-1 h-px bg-neutral-200" />
                        <span className="mx-3 text-xs text-neutral-500">
                            {t('RegisterLoginPage.RegisterForm.haveAccountYet')}
                        </span>
                        <div className="flex-1 h-px bg-neutral-200" />
                    </div>

                    <button
                        type="button"
                        onClick={() => setSection('Login')}
                        className="w-full py-3.5 rounded-xl font-semibold text-base bg-[#225424] text-white hover:bg-[#1c4a1e] active:bg-[#1c4a1e] transition"
                    >
                        {t('RegisterLoginPage.RegisterForm.loginCta')}
                    </button>
                </>
            )}

            {fetchStatus === 'loading' && (
                <div className="py-12 flex justify-center"><Loader /></div>
            )}

            {fetchStatus === 'succeeded' && (
                <h3 className="!text-xl text-center py-12 text-[#225424]">
                    {t('RegisterLoginPage.RegisterForm.registerSucceeded')}
                </h3>
            )}

            {fetchStatus === 'failed' && error && (
                <div className="flex flex-col items-center py-6 gap-2">
                    <h3 className="!text-xl text-[#b05704] text-center">
                        {t('RegisterLoginPage.RegisterForm.registerFailed')}
                    </h3>
                    <p className="text-sm text-neutral-700 text-center">{error}</p>
                </div>
            )}
        </div>
    )
}

export default RegisterForm;
